import { createContext, useContext } from "react";

export type NubankFeatureFlagsContextProps = {
  // Maintenance
  appMaintenance?: boolean;
  appExcludingTripsMaintenance?: boolean;
  flightsMaintenance?: boolean;
  hotelsMaintenance?: boolean;
  tripsMaintenance?: boolean;

  // Fintech Flags
  enableCfar?: boolean;
  enableChfar?: boolean;
  enableDisruption?: boolean;
  enableDisruptionExerciseRebooking?: boolean;
  enableMissedConnection?: boolean;
  enablePriceFreeze?: boolean;
  enablePriceWatch?: boolean;
  enablePricePrediction?: boolean;
  enablePriceDropProtection?: boolean;
  enableDisruptionProductAvailability?: boolean;
  enableNubankDisruptionFE?: boolean;
  enableDisruptionExerciseRebook?: boolean;

  // Features
  enableAirExchange?: boolean;
  enableDarkMode?: boolean;
  enableAirShopV4?: boolean;
  enableLoadingSteps?: boolean;
  enableLodging?: boolean;
  enableHFv2?: boolean;
  enableWalletCredits?: boolean;
  enableWalletOffers?: boolean;
  enableLodgingMap?: boolean;
  enableLodgingExpressCheckout?: boolean;
  enableExtraBags?: boolean;
  enableFeaturedOffers?: boolean;
  enableWalletOfferUrls?: boolean;
  enableEnhancedEarn?: boolean;
  enableEnhancedEarnPointsBackRate?: boolean;
  enableEarnPercentage?: boolean;
  enableVipSupport?: boolean;
  enableCentralizedErrorConfig?: boolean;
  enableNupayDirect?: boolean;
  enableCsatSurvey?: boolean;
  enableExternalTripsList?: boolean;

  // Debug / Testing
  /** When true, allows the user to access the debug menu in production via the 5-tap gesture. */
  enableDebugMenuAccess?: boolean;
  /** When true, disables the iOS touchstart workaround (preventScroll focus fix).
   *  Used to test whether the Nubank miniapp CSS fix resolves the jitter without our workaround. */
  disableTouchFix?: boolean;

  // Experiments
  hideAirItineraryReview?: boolean;
  hideFareDetails?: boolean;
  enableFintechSelection?: boolean;
  seatsAvailable?: boolean;
  enableVouchers10?: boolean;
  enableVouchers20?: boolean;
  replaceMaterialUI?: boolean;
  renderLodgingStrikethroughPrice?: boolean;
  renderHotelMapEntryPoint?: boolean;
  nubankMaxInstallments?: string;
  enableBlackFridayExperiment?: boolean;
  enableInstallmentsCampaign?: string;
  renderLodgingShopValueProps?: string;
  renderHotelMapAsDefaultView?: boolean;
  renderExtraHotelAmenities?: string;
  enableTravelInsurance?: boolean;
  enableBookingStatusCallout?: boolean;
  enableHotelDetailsCtaCopy?: boolean;
  roomDetailsCtaCopy?: string;
  /**
   * Passenger form modal variant:
   * - "control" or undefined: Original MUI Dialog + TravelerInfoForm
   * - "native-textfields": NubankSimpleModal + NativeInput (recommended for iOS Safari)
   */
  useSimplePassengerFormModal?: string;
  /**
   * Input focus handling variant for iOS Safari jitter fix:
   * - "control" or undefined: No focus handling (native behavior)
   * - "scroll-into-view": Use scrollIntoView after focus
   * - "delayed-scroll": Use delayed scrollIntoView (after keyboard appears)
   * - "android-only": Only scrollIntoView on Android (skip on iOS)
   */
  inputFocusHandling?: string;
};

interface INubankFeatureFlagsContext extends NubankFeatureFlagsContextProps {
  setOverride: (key: string, value: boolean | string) => void;
  resetOverrides: () => void;
}

export const NubankFeatureFlagsContext =
  createContext<INubankFeatureFlagsContext>({
    setOverride: () => {},
    resetOverrides: () => {},
  });

export const useNubankFeatureFlagsContext = () =>
  useContext(NubankFeatureFlagsContext);
